import type { HoldCleaningReport } from "../models/hold-cleaning.models";
import { getHoldCleaningErrorMessage, getHoldCleaningReports } from "../services/hold-cleaning.service";
import { formatHoldCleaningDate } from "./HoldCleaningUi";
import { useCallback, useEffect, useRef, useState } from "react";

export const HoldCleaningReports = ({ voyageId }: { voyageId: string }) => {
  const [reports, setReports] = useState<HoldCleaningReport[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);

  const load = useCallback(
    async (after: string | undefined, request: number) => {
      setLoading(true);
      setError(null);
      try {
        const result = await getHoldCleaningReports(voyageId, after);
        if (generation.current !== request) return;
        setReports((previous) => (after ? [...previous, ...result.items] : result.items));
        setCursor(result.nextCursor);
      } catch (failure) {
        if (generation.current === request) setError(getHoldCleaningErrorMessage(failure));
      } finally {
        if (generation.current === request) setLoading(false);
      }
    },
    [voyageId],
  );

  useEffect(() => {
    const request = ++generation.current;
    setReports([]);
    setCursor(null);
    void load(undefined, request);
    return () => {
      generation.current += 1;
    };
  }, [load]);

  return (
    <div className="mt-4 space-y-3">
      {reports.map((report, index) => (
        <details
          key={report.emailIdentityHash}
          open={index === 0}
          className="rounded-lg border border-[#DFE4E2] p-3 dark:border-gray-700"
        >
          <summary className="cursor-pointer text-sm font-semibold">
            {formatHoldCleaningDate(report.receivedAt)}
            {index === 0 ? " · Latest report" : ""}
          </summary>
          <p className="mt-3 whitespace-pre-wrap break-words text-sm">
            {report.body || "Report information was provided in attachments."}
          </p>
          {report.truncated && (
            <p className="mt-2 text-xs text-amber-800 dark:text-amber-200">
              This report exceeds the display limit and has been shortened.
            </p>
          )}
          {report.attachmentCount > 0 && (
            <p className="mt-2 text-xs text-[#4A5655] dark:text-gray-300">
              {report.attachmentCount} attachment document(s) included in processing.
            </p>
          )}
          {report.sourceUrl && (
            <a
              className="mt-2 inline-block text-sm text-[#176B62] underline dark:text-teal-200"
              href={report.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open original in Shipfix
            </a>
          )}
        </details>
      ))}
      {loading && <p className="text-sm text-[#4A5655] dark:text-gray-300">Loading reports…</p>}
      {error && (
        <div role="alert" className="text-sm text-red-700 dark:text-red-300">
          {error}{" "}
          <button
            type="button"
            onClick={() => void load(cursor ?? undefined, generation.current)}
            className="underline"
          >
            Retry
          </button>
        </div>
      )}
      {!loading && !error && !reports.length && (
        <p className="text-sm text-[#4A5655] dark:text-gray-300">
          No saved email reports for this voyage yet. Existing extracted work entries are shown below.
        </p>
      )}
      {cursor && (
        <button
          type="button"
          disabled={loading}
          onClick={() => void load(cursor, generation.current)}
          className="text-sm text-[#176B62] underline dark:text-teal-200"
        >
          Load older reports
        </button>
      )}
    </div>
  );
};
